/*
 *
 * ApmConfigurationModal
 *
 * @version 1.0.0
 * @author Stefano Servodidio
 * @since 14-Jan-2025
 */

import React, { useEffect, useMemo, useState } from 'react'
import PropTypes from 'prop-types'
import { useIntl } from 'react-intl'
import { useFormik } from 'formik'
import * as Yup from 'yup'
import { Box } from '@mui/material'
import MerchantGenericCard from '@nexidigital/merchantgenericcard'
import MerchantModal from '@nexidigital/merchantmodal'
import './ApmConfigurationModal.scss'
import PortalButton from '@nexidigital/portalbutton'
import PortalCheckBox from '@nexidigital/portalcheckbox'
import PortalLoader from '@nexidigital/portalloader'
import PortalTextInput from '@nexidigital/portaltextinput'
import PortalTextLink from '@nexidigital/portaltextlink'
import { findApmParamByName } from 'Utility/globalMethods'
import ApmSwitch from 'Components/ApmSwitch'
import { APM } from 'Schemas/apm'
import HeylightFields from './fields/HeylightFields'
import PaypalFields from './fields/PaypalFields'
import PProFields from './fields/PProFields'
import formattedMessage, { messages } from './languageModule'

const ApmConfigurationModal = ({ apmId, apmList, paypalPayIn3, loadingList, loadingApm, actions }) => {
  const intl = useIntl()

  const {
    apm,
    logo,
    id,
    serviceProvider,
    name: label,
    description,
    termsOfServiceUrl
  } = useMemo(() => {
    const apm = apmList.find?.((apm) => apm?.feature?.name === apmId) || {}
    const { name, logo } = apm?.feature || {}
    const id = apmId === 'Satispay' ? 'SATISPAY_DIRECT' : name?.toUpperCase?.()
    return { apm, name, logo, id, ...(APM[id] || {}) }
  }, [apmId, apmList])

  // PAYPAL
  const paypalCallbackUrl = findApmParamByName(apm?.params, 'PAYPALCALLBACKURL')?.value
  const requiresPaypalSubscription = id === 'PAYPAL' && apm?.status === 'Disabled' && paypalCallbackUrl

  useEffect(() => {
    // Prepare the single-use PayPal URL and SDK before confirmation to preserve the click's popup permission.
    if (requiresPaypalSubscription) actions.doGetPaypalExternalLink({ url: paypalCallbackUrl })
  }, [requiresPaypalSubscription, paypalCallbackUrl])

  // satispayDirect
  const satispayTokenParam = findApmParamByName(apm?.params, 'TOKEN')?.value || ''

  // ALIPAY PLUS
  const referenceMerchantId = findApmParamByName(apm?.params, 'REFERENCEMERCHANTID')?.value
  const [showMore, setShowMore] = useState(false)

  const initialValues = {
    status: 'Disabled',
    accept: id === 'KLARNA', // accept is true by default for klarna
    // paypal
    payIn3Status: 'Disabled',
    // ppro
    contractId: '',
    // heylight
    merchantKey: '',
    // satispayDirect
    token: ''
  }

  const merchantKeyParam = findApmParamByName(apm?.params, 'PAGOLIGHTMERCHANTKEY')
  const savedMerchantKey = merchantKeyParam?.value && merchantKeyParam.value !== 'null' ? merchantKeyParam.value : ''

  const maskHeylightMerchantKey = (key) => key && `***${key.slice(3)}`

  const formik = useFormik({
    initialValues,
    validationSchema: Yup.object({
      accept: Yup.boolean().oneOf([true], intl.formatMessage(messages.pProFieldsAcceptError)),
      contractId:
        serviceProvider === 'PPRO'
          ? Yup.string().required(intl.formatMessage(messages.pProFieldsContractIdError))
          : Yup.string(),
      merchantKey:
        id === 'HEYLIGHT'
          ? Yup.string()
            .test(
              'isAlphaNumericAndLength40',
              intl.formatMessage(messages.heylightFieldsMerchantKeyLengthError),
              (value) =>
                value ? value === maskHeylightMerchantKey(savedMerchantKey) || /^[a-zA-Z0-9]{40}$/.test(value) : true
            )
            .required(intl.formatMessage(messages.heylightFieldsMerchantKeyRequiredError))
          : Yup.string(),
      token:
        id === 'SATISPAY_DIRECT'
          ? Yup.string().required(intl.formatMessage(messages.isRequired))
          : Yup.string()
    })
  })

  const isValid = useMemo(() => {
    const { status, accept, payIn3Status, merchantKey, contractId, token } = formik.values || {}
    let hasChanged = status !== apm?.status // check if status has changed

    if (id === 'PAYPAL' && apm?.status === 'Enabled') {
      hasChanged = hasChanged || payIn3Status !== paypalPayIn3?.status // check if payIn3Status has changed
    } else if (id === 'HEYLIGHT') {
      hasChanged = hasChanged || merchantKey !== maskHeylightMerchantKey(savedMerchantKey) // check if merchantKey has changed
    } else if (serviceProvider === 'PPRO') {
      hasChanged = hasChanged || contractId !== findApmParamByName(apm?.params, 'PPROCONTRACTID')?.value // check if contractId has changed
    } else if (id === 'SATISPAY_DIRECT') {
      hasChanged = hasChanged || satispayTokenParam !== token
    }

    return hasChanged && accept && formik.isValid
  }, [apm, paypalPayIn3, initialValues, formik])
  /** LIFECYCLE */
  useEffect(() => {
    formik.setValues({
      status: apm?.status === 'Enabled' ? 'Enabled' : 'Disabled',
      accept: apm?.status === 'Enabled' || id === 'KLARNA' || id === 'ALIPAY_PLUS', // accept is true by default for klarna and alipay+
      // paypal
      payIn3Status: paypalPayIn3?.status || undefined,
      // ppro
      contractId: findApmParamByName(apm?.params, 'PPROCONTRACTID')?.value || '',
      // heylight
      merchantKey: savedMerchantKey ? maskHeylightMerchantKey(savedMerchantKey) : '',
      // satispayDirect
      token: satispayTokenParam || ''
    })
  }, [apm, paypalPayIn3])

  // METHODS
  const onSuccess = () => {
    actions.togglePopup('')
    actions.togglePopup('RESULT_POPUP', {
      title: formattedMessage.successTitle,
      description: formattedMessage.successDescription,
      isSuccess: true
    })
  }
  const onError = () => {
    actions.togglePopup('')
    actions.togglePopup('RESULT_POPUP', {
      title: formattedMessage.failureTitle,
      description: formattedMessage.failureDescription,
      isError: true
    })
  }

  const submitApmGeneric = () => {
    const updatedApm = {
      featureName: apm?.feature?.name,
      status: formik.values.status,
      subscriptionParams: apm?.params?.map((param) => ({ ...param })) || []
    }

    if (id === 'ALIPAY_PLUS') {
      const paramReferenceMerchantId = findApmParamByName(updatedApm?.subscriptionParams, 'REFERENCEMERCHANTID')
      if (paramReferenceMerchantId) paramReferenceMerchantId.value = referenceMerchantId
    }

    if (serviceProvider === 'PPRO') {
      const paramPproContractId = findApmParamByName(updatedApm?.subscriptionParams, 'PPROCONTRACTID')
      if (paramPproContractId) paramPproContractId.value = formik.values.contractId
    }

    if (id === 'HEYLIGHT') {
      const paramHeylightMerchantKey = findApmParamByName(updatedApm?.subscriptionParams, 'PAGOLIGHTMERCHANTKEY')
      const newKey = formik.values.merchantKey
      if (paramHeylightMerchantKey?.value && newKey !== maskHeylightMerchantKey(paramHeylightMerchantKey.value)) {
        paramHeylightMerchantKey.value = newKey
      }
    }
    if (id === 'SATISPAY_DIRECT') {
      const paramSatispayToken = findApmParamByName(updatedApm?.subscriptionParams, 'TOKEN')
      if (paramSatispayToken && satispayTokenParam !== formik.values.token) {
        paramSatispayToken.value = formik.values.token
      }
    }

    actions.doUpdateApm({
      payload: updatedApm,
      subscriptionId: apm?.id,
      onSuccess,
      onError
    })
  }

  const submitPaypal = () => {
    const batch = []

    if (apm?.status !== formik.values?.status) {
      batch.push({
        payload: {
          featureName: apm?.feature?.name,
          status: formik.values.status,
          subscriptionParams: apm?.params || []
        },
        subscriptionId: apm?.id
      })
    }

    if (
      paypalPayIn3?.status &&
      paypalPayIn3?.status !== formik.values.payIn3Status &&
      formik.values.status !== 'Disabled'
    ) {
      batch.push({
        payload: {
          featureName: paypalPayIn3?.feature?.name,
          status: formik.values.payIn3Status,
          subscriptionParams: paypalPayIn3?.params || []
        },
        subscriptionId: paypalPayIn3?.id
      })
    }
    actions.doUpdateApm(batch, { onSuccess, onError })
  }

  // HANDLERS
  const handleConfirm = () => {
    if (requiresPaypalSubscription) {
      const paypalButton = document.querySelector('a[data-paypal-button="true"][data-paypal-ready="true"]')
      // PayPal opens its mini browser from this SDK-bound link; keep the click in the user's activation.
      if (paypalButton) return paypalButton.click()
      return actions.doGetPaypalExternalLink({ url: paypalCallbackUrl })
    }
    if (id === 'PAYPAL') {
      return submitPaypal()
    }
    submitApmGeneric()
  }

  const handleSwitchStatus = (toggle) => {
    if (id === 'PAYPAL' && !toggle && formik.values.payIn3Status === 'Enabled') {
      formik.setFieldValue('payIn3Status', 'Disabled')
    }
    formik.setFieldValue('status', toggle ? 'Enabled' : 'Disabled')
  }
 
  return (
    <MerchantModal
      title={intl.formatMessage(messages.title)}
      primaryButtonLabel={formattedMessage.confirm}
      primaryAction={handleConfirm}
      primaryButtonDisabled={!isValid}
      primaryButtonLoading={loadingApm}
      closeLabel={intl.formatMessage(messages.close)}
      onClose={() => {
        // Resetting the URL lets the hook remove its SDK scripts and trigger link.
        if (requiresPaypalSubscription) actions.doResetPaypalExternalLink()
        actions.togglePopup('')
        actions.doSetSelectedApmId('')
      }}>
      <MerchantGenericCard>
        <div className='apm_configuration_wrapper'>
          {loadingList ? (
            <div className='apmConfigurationModal_loader'>
              <PortalLoader />
            </div>
          ) : (
            <>
              {/* ENABLE / DISABLE APM */}
              <ApmSwitch
                checked={formik.values.status === 'Enabled'}
                onChange={handleSwitchStatus}
                disabled={loadingApm}
                ariaLabel={intl.formatMessage(messages.apmSwitchAriaLabel)}
                {...{ label, description, logo }}
              />

              {/* EXTRA FIELDS */}
              {serviceProvider === 'PPRO' && <PProFields {...{ formik }} loading={loadingApm} />}
              {id === 'HEYLIGHT' && (
                <HeylightFields
                  {...{ formik }}
                  initialValue={maskHeylightMerchantKey(savedMerchantKey)}
                  loading={loadingApm}
                />
              )}
              {id === 'PAYPAL' && !requiresPaypalSubscription && (
                <PaypalFields loading={loadingApm} {...{ apm, paypalPayIn3, formik }} />
              )}

              {id === 'SATISPAY_DIRECT' && (
                <PortalTextInput
                  id='satispayDirect_token'
                  label={formattedMessage.satispayDirectFieldsTokenLabel}
                  value={formik.values.token}
                  onChange={(e) => formik.setFieldValue('token', e.target.value)}
                  onBlur={() => formik.setFieldTouched('token', true)}
                  error={formik.touched.token && formik.errors.token}
                  errorText={formik.errors.token}
                  placeholderBlank
                />
              )}

              {id === 'ALIPAY_PLUS' && (
                <PortalTextInput
                  label={formattedMessage.merchantReferenceId}
                  value={referenceMerchantId || '-'}
                  disabled
                />
              )}
              {/* TERMS AND CONDITIONS */}
              {(id !== 'KLARNA' && id !== 'ALIPAY_PLUS') && (
                <div className='configuration_wrapper'>
                  <PortalCheckBox
                    centered
                    options={[
                      {
                        id: 'accept_conditions',
                        value: 'accept_conditions',
                        label: termsOfServiceUrl ? (
                          <span className='accept_conditions'>
                            {intl.formatMessage(messages.accept)}&nbsp;
                            <PortalTextLink
                              href={termsOfServiceUrl}
                              label={intl.formatMessage(messages.link)}
                              underline='always'
                            />
                          </span>
                        ) : (
                          <span>{intl.formatMessage(messages.acceptPpro)}</span>
                        ),
                        checked: !!formik.values.accept,
                        disabled: !!loadingApm || formik.values.status === 'Disabled' || apm?.status === 'Enabled',
                        required: true,
                        error: !!formik.errors.accept
                      }
                    ]}
                    onChange={(obj) => formik.setFieldValue('accept', obj?.checked)}
                    handleChange={() => {}}
                  />
                  <div className='configuration_disclaimer_wrapper'>
                    <Box
                      variant='caption'
                      className='configuration_disclaimer'
                      sx={
                        !showMore && {
                          overflow: 'hidden',
                          textOverflow: 'ellipsis',
                          display: '-webkit-box',
                          WebkitLineClamp: '2',
                          WebkitBoxOrient: 'vertical'
                        }
                      }>
                      {formattedMessage.termsAndConditionsText}
                    </Box>
                    <PortalButton
                      id='configuration_disclaimer_showMore'
                      label={showMore ? intl.formatMessage(messages.showLess) : intl.formatMessage(messages.showMore)}
                      onClick={() => {
                        setShowMore(!showMore)
                      }}
                      size='small'
                      textButton
                    />
                  </div>
                </div>
              )}
            </>
          )}
        </div>
      </MerchantGenericCard>
    </MerchantModal>
  )
}

ApmConfigurationModal.propTypes = {
  apmId: PropTypes.string,
  apmList: PropTypes.array,
  paypalPayIn3: PropTypes.object,
  actions: PropTypes.object,
  loadingApm: PropTypes.bool,
  loadingList: PropTypes.bool
}

ApmConfigurationModal.defaultProps = {
  apmList: [],
  paypalPayIn3: null,
  loadingApm: false,
  loadingList: false
}

export default ApmConfigurationModal
